import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Plus } from "lucide-react"
import { mlSheetService } from "../../services/mlSheetService"
import vehicleService from "../../services/vehicleService.js"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { PageLayout, Button, DetailCard, FormField } from "../common/index.js"

const AddMLSheet = () => {
  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchInputs, setSearchInputs] = useState({
    from: "",
    to: "",
    vehicle: "",
    access: "",
  })
  const fromRef = useRef(null)
  const toRef = useRef(null)
  const vehicleRef = useRef(null)
  const accessRef = useRef(null)
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [searchingShipments, setSearchingShipments] = useState(false)
  const [agents, setAgents] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [locations, setLocations] = useState([])
  const [filteredShipments, setFilteredShipments] = useState([])
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([])
  const [selectAllChecked, setSelectAllChecked] = useState(false)

  const [formData, setFormData] = useState({
    from_location_ids: [],
    to_location_ids: [],
    from_date: "",
    to_date: "",
    vehicle_id: "",
    driver_contact: "",
    access_user_ids: [],
  })

  const [tempVehicles, setTempVehicles] = useState([])
  const [showAddVehicleForm, setShowAddVehicleForm] = useState(false)
  const [tempVehicleData, setTempVehicleData] = useState({
    vehicle_number: "",
    vehicle_name: "",
    vehicle_type: "cargo_goods",
    chassis_no: "",
  })

  useEffect(() => {
    fetchInitialData()
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !fromRef.current?.contains(e.target) &&
        !toRef.current?.contains(e.target) &&
        !vehicleRef.current?.contains(e.target) &&
        !accessRef.current?.contains(e.target)
      ) {
        setOpenDropdown(null)
        setSearchInputs({
          from: "",
          to: "",
          vehicle: "",
          access: "",
        })
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    searchShipments()
  }, [formData.from_location_ids, formData.to_location_ids, formData.from_date, formData.to_date])

  const fetchInitialData = async () => {
    try {
      setLoading(true)
      const [agentsRes, vehiclesRes, locationsRes] = await Promise.all([
        mlSheetService.getAgents(),
        mlSheetService.getAllVehiclesNoPage(),
        mlSheetService.getLocations(),
      ])

      console.log("[Dynamic] Agents Response:", agentsRes.data)
      console.log("[Dynamic] Vehicles Response:", vehiclesRes.data)
      console.log("[Dynamic] Locations Response:", locationsRes.data)

      const agentsData = agentsRes.data.data || agentsRes.data.users || agentsRes.data || []
      const vehiclesData = vehiclesRes.data.data || vehiclesRes.data.vehicles || vehiclesRes.data || []
      const locationsData = locationsRes.data.hubs || locationsRes.data.data || locationsRes.data || []

      console.log("[Dynamic] Extracted Agents:", agentsData)
      console.log("[Dynamic] Extracted Vehicles:", vehiclesData)
      console.log("[Dynamic] Extracted Locations:", locationsData)

      setAgents(Array.isArray(agentsData) ? agentsData : [])
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : [])
      setLocations(Array.isArray(locationsData) ? locationsData : [])

      if (user?.role === "Agent" && user?.user_id) {
        setFormData((prev) => ({
          ...prev,
          access_user_ids: prev.access_user_ids.includes(user.user_id)
            ? prev.access_user_ids
            : [...prev.access_user_ids, user.user_id],
        }))
      }

      if (user?.city && Array.isArray(locationsData)) {
        const userLocationObj = locationsData.find(
          (loc) => (loc.location || loc.hub_name || loc.name) === user.city
        )
        if (userLocationObj) {
          const locId = userLocationObj.id || userLocationObj.hub_id
          setFormData((prev) => ({
            ...prev,
            from_location_ids: [locId],
          }))
          console.log("[Dynamic] Auto-selected user city:", user.city, "with ID:", locId)
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      Alert.error("Failed to load initial data")
    } finally {
      setLoading(false)
    }
  }

  const searchShipments = async () => {
    if (
      formData.from_location_ids.length === 0 &&
      formData.to_location_ids.length === 0 &&
      !formData.from_date &&
      !formData.to_date
    ) {
      setFilteredShipments([])
      setSelectedShipmentIds([])
      setSelectAllChecked(false)
      return
    }

    try {
      setSearchingShipments(true)

      const searchParams = {}

      if (formData.from_location_ids.length > 0) {
        const fromLocations = formData.from_location_ids
          .map((id) => {
            const loc = locations.find((l) => (l.id || l.hub_id) === id)
            return loc ? loc.location || loc.hub_name || loc.name : null
          })
          .filter(Boolean)
        if (fromLocations.length > 0) {
          searchParams.from_location = fromLocations.join(",")
        }
      }

      if (formData.to_location_ids.length > 0) {
        const toLocations = formData.to_location_ids
          .map((id) => {
            const loc = locations.find((l) => (l.id || l.hub_id) === id)
            return loc ? loc.location || loc.hub_name || loc.name : null
          })
          .filter(Boolean)
        if (toLocations.length > 0) {
          searchParams.to_location = toLocations.join(",")
        }
      }

      if (formData.from_date) searchParams.from_date = formData.from_date
      if (formData.to_date) searchParams.to_date = formData.to_date
      if (formData.from_date) searchParams.from_date = formData.from_date
      if (formData.to_date) searchParams.to_date = formData.to_date

      console.log("[Dynamic] Searching shipments with params:", searchParams)

      const response = await mlSheetService.getBookedShipments(searchParams)

      console.log("[Dynamic] Shipments Response:", response.data)

      const shipmentsData = response.data.shipments || response.data.data || response.data || []

      console.log("[Dynamic] Extracted Shipments:", shipmentsData)

      setFilteredShipments(Array.isArray(shipmentsData) ? shipmentsData : [])

      setSelectedShipmentIds([])
      setSelectAllChecked(false)
    } catch (error) {
      console.error("Error searching shipments:", error)
      setFilteredShipments([])
    } finally {
      setSearchingShipments(false)
    }
  }

  const handleLocationToggle = (locationType, locationId) => {
    setFormData((prev) => {
      const key = locationType === "from" ? "from_location_ids" : "to_location_ids"
      const currentIds = prev[key]

      if (currentIds.includes(locationId)) {
        return { ...prev, [key]: currentIds.filter((id) => id !== locationId) }
      } else {
        return { ...prev, [key]: [...currentIds, locationId] }
      }
    })
  }

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSelectShipment = (shipmentId) => {
    setSelectedShipmentIds((prev) =>
      prev.includes(shipmentId) ? prev.filter((id) => id !== shipmentId) : [...prev, shipmentId]
    )
  }

  const handleSelectAll = () => {
    if (selectAllChecked) {
      setSelectedShipmentIds([])
    } else {
      setSelectedShipmentIds(filteredShipments.map((s) => s.shipment_id))
    }
    setSelectAllChecked(!selectAllChecked)
  }

  const handleVehicleChange = (e) => {
    const { value } = e.target
    const allVehicles = getCombinedVehicles()
    const selectedVehicle = allVehicles.find(
      (v) => v.id === value || v.vehicle_id === value || String(v.id) === String(value)
    )

    setFormData({
      ...formData,
      vehicle_id: selectedVehicle?.id || selectedVehicle?.vehicle_id || value,
      driver_contact: selectedVehicle?.driver_contact || "",
    })
  }

  const handleDriverContactChange = (e) => {
    setFormData({
      ...formData,
      driver_contact: e.target.value,
    })
  }

  const handleAccessUserChange = (userId) => {
    setFormData((prev) => ({
      ...prev,
      access_user_ids: prev.access_user_ids.includes(userId)
        ? prev.access_user_ids.filter((id) => id !== userId)
        : [...prev.access_user_ids, userId],
    }))
  }

  const handleSearchInput = (dropdownType, value) => {
    setSearchInputs((prev) => ({
      ...prev,
      [dropdownType]: value,
    }))
  }

  const getFilteredLocations = (searchTerm) => {
    return locations.filter((loc) => {
      const locName = (loc.location || loc.hub_name || loc.name || "").toLowerCase()
      return locName.includes(searchTerm.toLowerCase())
    })
  }

  const getFilteredVehicles = (searchTerm) => {
    const allVehicles = getCombinedVehicles()
    return allVehicles.filter((vehicle) => {
      const vehicleNumber = (vehicle.vehicle_number || "").toLowerCase()
      const vehicleType = (vehicle.vehicle_type || "").toLowerCase()
      const searchLower = searchTerm.toLowerCase()
      return vehicleNumber.includes(searchLower) || vehicleType.includes(searchLower)
    })
  }

  const getFilteredAgents = (searchTerm) => {
    return agents.filter((agent) => {
      const agentName = (agent.name || "").toLowerCase()
      return agentName.includes(searchTerm.toLowerCase())
    })
  }

  const handleTempVehicleChange = (e) => {
    const { name, value } = e.target
    setTempVehicleData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleAddTempVehicle = async () => {
    if (!tempVehicleData.vehicle_number.trim()) {
      Alert.error("Vehicle number is required")
      return
    }
    if (!tempVehicleData.vehicle_name.trim()) {
      Alert.error("Vehicle name is required")
      return
    }

    try {
      setLoading(true)

      const vehicleFormData = new FormData()
      vehicleFormData.append("vehicle_name", tempVehicleData.vehicle_name)
      vehicleFormData.append("vehicle_number", tempVehicleData.vehicle_number.toUpperCase())
      vehicleFormData.append("vehicle_type", tempVehicleData.vehicle_type)
      if (tempVehicleData.chassis_no) {
        vehicleFormData.append("chassis_no", tempVehicleData.chassis_no)
      }
      vehicleFormData.append("status", "active")

      console.log("[Dynamic] Creating new vehicle:", tempVehicleData)

      const vehicleResponse = await vehicleService.createVehicle(vehicleFormData)
      console.log("[Dynamic] Vehicle created successfully:", vehicleResponse.data)

      const createdVehicle = vehicleResponse.data.data
      const newVehicleId = createdVehicle.id || createdVehicle.vehicle_id

      setVehicles((prev) => [...prev, createdVehicle])

      setFormData((prev) => ({
        ...prev,
        vehicle_id: newVehicleId,
      }))

      setTempVehicleData({
        vehicle_number: "",
        vehicle_name: "",
        vehicle_type: "cargo_goods",
        chassis_no: "",
      })
      setShowAddVehicleForm(false)
      setOpenDropdown(null)
      setSearchInputs((prev) => ({ ...prev, vehicle: "" }))

      Alert.success("Vehicle created successfully")
    } catch (error) {
      console.error("Error creating vehicle:", error)
      Alert.error(error.response?.data?.error || "Failed to create vehicle")
    } finally {
      setLoading(false)
    }
  }

  const getCombinedVehicles = () => {
    return [...vehicles, ...tempVehicles]
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.from_location_ids.length === 0) {
      Alert.error("Please select at least one from location")
      return
    }

    if (formData.to_location_ids.length === 0) {
      Alert.error("Please select at least one to location")
      return
    }

    if (!formData.vehicle_id) {
      Alert.error("Please select a vehicle")
      return
    }

    if (selectedShipmentIds.length === 0) {
      Alert.error("Please select at least one shipment")
      return
    }

    if (formData.access_user_ids.length === 0 && user?.role !== "Agent") {
      Alert.error("Please select at least one access user")
      return
    }

    try {
      setLoading(true)

      const payload = {
        from_location_ids: formData.from_location_ids,
        to_location_ids: formData.to_location_ids,
        vehicle_id: formData.vehicle_id,
        driver_contact: formData.driver_contact || "N/A",
        shipment_ids: selectedShipmentIds,
        access_user_ids: formData.access_user_ids,
      }

      console.log("[Dynamic] Creating ML Sheet with payload:", payload)

      await mlSheetService.createMLSheet(payload)
      Alert.success("ML Sheet created successfully")
      navigate("/ml-sheets")
    } catch (error) {
      console.error("Error creating ML Sheet:", error)
      Alert.error(error.response?.data?.error || "Failed to create ML Sheet")
    } finally {
      setLoading(false)
    }
  }

  if (loading && agents.length === 0 && vehicles.length === 0 && locations.length === 0) {
    return (
      <PageLayout title="Create Master Loading Sheet" maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Create Master Loading Sheet"
      maxWidth="max-w-7xl"
      actions={
        <>
          <Button onClick={handleSubmit} loading={loading}>
            Create
          </Button>
          <Button variant="secondary" onClick={() => navigate("/ml-sheets")}>
            Cancel
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DetailCard title="Location & Date Filters">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormField label="From Locations" required>
                <div className="relative" ref={fromRef}>
                  <div
                    onClick={() => setOpenDropdown(openDropdown === "from" ? null : "from")}
                    className="cursor-pointer border border-gray-300 rounded-sm px-3 py-2 bg-white flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-700">
                      {formData.from_location_ids.length > 0
                        ? `${formData.from_location_ids.length} selected`
                        : "Select locations"}
                    </span>
                    <span className={`transition ${openDropdown === "from" ? "rotate-180" : ""}`}>
                      ▾
                    </span>
                  </div>

                  {openDropdown === "from" && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-sm shadow p-2">
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={searchInputs.from}
                        onChange={(e) => handleSearchInput("from", e.target.value)}
                        autoFocus
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                      />
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredLocations(searchInputs.from).map((loc) => {
                          const locId = loc.id || loc.hub_id
                          return (
                            <label
                              key={locId}
                              className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.from_location_ids.includes(locId)}
                                onChange={() => handleLocationToggle("from", locId)}
                                className="w-4 h-4"
                              />
                              <span className="ml-3 text-sm">
                                {loc.location || loc.hub_name || loc.name}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {formData.from_location_ids.length}
                  </p>
                </div>
              </FormField>

              <FormField label="To Locations" required>
                <div className="relative" ref={toRef}>
                  <div
                    onClick={() => setOpenDropdown(openDropdown === "to" ? null : "to")}
                    className="cursor-pointer border border-gray-300 rounded-sm px-3 py-2 bg-white flex justify-between items-center"
                  >
                    <span className="text-sm text-gray-700">
                      {formData.to_location_ids.length > 0
                        ? `${formData.to_location_ids.length} selected`
                        : "Select locations"}
                    </span>
                    <span
                      className={`text-gray-400 transition ${openDropdown === "to" ? "rotate-180" : ""}`}
                    >
                      ▾
                    </span>
                  </div>

                  {openDropdown === "to" && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-sm shadow p-2">
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={searchInputs.to}
                        onChange={(e) => handleSearchInput("to", e.target.value)}
                        autoFocus
                        className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                      />
                      <div className="max-h-48 overflow-y-auto">
                        {Array.isArray(locations) && getFilteredLocations(searchInputs.to).length > 0 ? (
                          getFilteredLocations(searchInputs.to).map((loc) => {
                            const locId = loc.id || loc.hub_id
                            return (
                              <label
                                key={locId}
                                className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.to_location_ids.includes(locId)}
                                  onChange={() => handleLocationToggle("to", locId)}
                                  className="w-4 h-4"
                                />
                                <span className="ml-3 text-sm font-medium text-gray-800">
                                  {loc.location || loc.hub_name || loc.name}
                                  <span className="text-xs text-gray-500 ml-2">
                                    ({loc.destination_type})
                                  </span>
                                </span>
                              </label>
                            )
                          })
                        ) : (
                          <p className="text-sm text-gray-500">No locations available</p>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {formData.to_location_ids.length}
                  </p>
                </div>
              </FormField>

              <FormField label="From Date">
                <input
                  type="date"
                  name="from_date"
                  value={formData.from_date}
                  onChange={handleDateChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand"
                />
              </FormField>

              <FormField label="To Date">
                <input
                  type="date"
                  name="to_date"
                  value={formData.to_date}
                  onChange={handleDateChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand"
                />
              </FormField>
            </div>
          </DetailCard>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
            <DetailCard title="Vehicle & Driver Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Vehicle" required>
                  <div className="relative" ref={vehicleRef}>
                    <div
                      onClick={() => setOpenDropdown(openDropdown === "vehicle" ? null : "vehicle")}
                      className="cursor-pointer border border-gray-300 rounded-sm px-3 py-2 bg-white flex justify-between items-center"
                    >
                      <span className="text-sm text-gray-700">
                        {formData.vehicle_id
                          ? (() => {
                              const allVehicles = getCombinedVehicles()
                              const vehicle = allVehicles.find(
                                (v) => (v.id || v.vehicle_id) === formData.vehicle_id
                              )
                              return vehicle
                                ? `${vehicle.vehicle_number} - ${vehicle.vehicle_type || ""}${vehicle.isTemporary ? " (Temp)" : ""}`
                                : "Select a vehicle"
                            })()
                          : "Select a vehicle"}
                      </span>
                      <span
                        className={`transition ${openDropdown === "vehicle" ? "rotate-180" : ""}`}
                      >
                        ▾
                      </span>
                    </div>

                    {openDropdown === "vehicle" && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-sm shadow p-2">
                        <input
                          type="text"
                          placeholder="Search vehicles..."
                          value={searchInputs.vehicle}
                          onChange={(e) => handleSearchInput("vehicle", e.target.value)}
                          autoFocus
                          className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                        />
                        <div className="max-h-48 overflow-y-auto">
                          {getFilteredVehicles(searchInputs.vehicle).length > 0 ? (
                            getFilteredVehicles(searchInputs.vehicle).map((v) => {
                              const vehicleId = v.id || v.vehicle_id
                              return (
                                <div
                                  key={vehicleId}
                                  onClick={() => {
                                    handleVehicleChange({ target: { value: vehicleId } })
                                    setOpenDropdown(null)
                                    setSearchInputs((prev) => ({ ...prev, vehicle: "" }))
                                  }}
                                  className="p-2 hover:bg-gray-50 rounded cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <span className="text-sm font-medium text-gray-800">
                                    {v.vehicle_number} - {v.vehicle_type || ""}
                                    {v.isTemporary && (
                                      <span className="ml-2 text-xs text-brand">(Temporary)</span>
                                    )}
                                  </span>
                                </div>
                              )
                            })
                          ) : (
                            <div className="p-2">
                              <p className="text-sm text-gray-500 mb-2">No vehicles found</p>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAddVehicleForm(true)
                                  setTempVehicleData((prev) => ({
                                    ...prev,
                                    vehicle_number: searchInputs.vehicle,
                                  }))
                                }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-brand text-white rounded-sm hover:bg-brand text-sm font-medium"
                              >
                                <Plus size={16} />
                                Add New Vehicle
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {showAddVehicleForm && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                        <div className="bg-white rounded-sm shadow-lg p-6 w-full max-w-md mx-4">
                          <h3 className="text-lg font-semibold text-secondary mb-4">
                            Add Temporary Vehicle
                          </h3>

                          <div className="space-y-4">
                            <FormField label="Vehicle Number" required>
                              <input
                                type="text"
                                name="vehicle_number"
                                value={tempVehicleData.vehicle_number}
                                onChange={handleTempVehicleChange}
                                placeholder="e.g., GJ01AB1234"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm uppercase"
                              />
                            </FormField>

                            <FormField label="Vehicle Name" required>
                              <input
                                type="text"
                                name="vehicle_name"
                                value={tempVehicleData.vehicle_name}
                                onChange={handleTempVehicleChange}
                                placeholder="e.g., Tata 407"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                              />
                            </FormField>

                            <FormField label="Vehicle Type" required>
                              <input
                                type="text"
                                name="vehicle_type"
                                value="cargo_goods"
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Vehicle type is set to cargo_goods by default
                              </p>
                            </FormField>

                            <FormField label="Chassis Number (Optional)">
                              <input
                                type="text"
                                name="chassis_no"
                                value={tempVehicleData.chassis_no}
                                onChange={handleTempVehicleChange}
                                placeholder="e.g., MAT123456789"
                                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                              />
                            </FormField>

                            <div className="flex gap-3 mt-6">
                              <Button type="button" onClick={handleAddTempVehicle} className="flex-1">
                                Add Vehicle
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  setShowAddVehicleForm(false)
                                  setTempVehicleData({
                                    vehicle_number: "",
                                    vehicle_name: "",
                                    vehicle_type: "cargo_goods",
                                    chassis_no: "",
                                  })
                                }}
                                className="flex-1"
                              >
                                Cancel
                              </Button>
                            </div>

                            <p className="text-xs text-gray-500 mt-2">
                              * This vehicle will be saved temporarily for this ML Sheet only
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </FormField>

                <FormField label="Driver Contact">
                  <input
                    type="text"
                    value={formData.driver_contact}
                    onChange={handleDriverContactChange}
                    placeholder="Enter driver contact number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand"
                  />
                </FormField>
              </div>
            </DetailCard>

            <DetailCard>
              <div className="relative" ref={accessRef}>
                <div className="flex items-center gap-4">
                  <FormField label="Select Access Users" required className="flex-1">
                    <div
                      onClick={() => setOpenDropdown(openDropdown === "access" ? null : "access")}
                      className="cursor-pointer border border-gray-300 rounded-sm px-3 py-2 bg-white flex justify-between items-center"
                    >
                      <span className="text-sm text-gray-700">
                        {formData.access_user_ids.length > 0
                          ? `${formData.access_user_ids.length} selected`
                          : "Select users"}
                      </span>
                      <span
                        className={`text-gray-400 transition ${openDropdown === "access" ? "rotate-180" : ""}`}
                      >
                        ▾
                      </span>
                    </div>
                  </FormField>
                </div>

                {openDropdown === "access" && (
                  <div className="absolute z-50 mt-1 right-0 w-[calc(100%-150px)] bg-white border border-gray-300 rounded-sm shadow p-2">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchInputs.access}
                      onChange={(e) => handleSearchInput("access", e.target.value)}
                      autoFocus
                      className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                    />
                    <div className="max-h-48 overflow-y-auto">
                      {Array.isArray(agents) && getFilteredAgents(searchInputs.access).length > 0 ? (
                        getFilteredAgents(searchInputs.access).map((agent) => {
                          const agentId = agent.user_id || agent.id
                          return (
                            <label
                              key={agentId}
                              className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formData.access_user_ids.includes(agentId)}
                                onChange={() => handleAccessUserChange(agentId)}
                                className="w-4 h-4"
                              />
                              <span className="ml-3 text-sm font-semibold text-secondary">
                                {agent.name}
                              </span>
                            </label>
                          )
                        })
                      ) : (
                        <p className="text-sm text-gray-500">No users available</p>
                      )}
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-1">
                  Selected: {formData.access_user_ids.length}
                </p>
              </div>
            </DetailCard>
          </div>
        </div>

        <DetailCard
          title={`Select Shipments (${selectedShipmentIds.length}/${filteredShipments.length})`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
            <div>
              {searchingShipments && (
                <p className="text-sm text-brand mt-1">Searching shipments...</p>
              )}
            </div>
            {filteredShipments.length > 0 && (
              <Button type="button" onClick={handleSelectAll}>
                {selectAllChecked ? "Deselect All" : "Select All"}
              </Button>
            )}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {searchingShipments ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
                <p className="text-gray-500 mt-2">Searching for shipments...</p>
              </div>
            ) : filteredShipments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {formData.from_location_ids.length > 0 ||
                  formData.to_location_ids.length > 0 ||
                  formData.from_date ||
                  formData.to_date
                    ? "No booked shipments found for selected filters"
                    : "Please select location or date filters to search for shipments"}
                </p>
              </div>
            ) : (
              filteredShipments.map((shipment) => (
                <label
                  key={shipment.shipment_id || shipment.id}
                  className="flex items-start p-3 border border-gray-200 rounded-sm hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedShipmentIds.includes(shipment.shipment_id || shipment.id)}
                    onChange={() => handleSelectShipment(shipment.shipment_id || shipment.id)}
                    className="mt-1 w-5 h-5 flex-shrink-0"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-bold text-gray-800">
                      {shipment.shipment_id || shipment.id}
                    </div>
                    <div className="text-sm text-gray-600">
                      {shipment.sender_name} → {shipment.receiver_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      From: {shipment.sender_location || "N/A"} | To:{" "}
                      {shipment.receiver_location || "N/A"}
                    </div>
                    <div className="text-sm text-gray-500">
                      {shipment.no_of_packages} packages | ₹{shipment.total_amount?.toFixed(2)} |
                      Created: {new Date(shipment.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </DetailCard>
      </form>
    </PageLayout>
  )
}

export default AddMLSheet
